import { useEffect, useState } from 'react'
import {
  Building2, Crown, Users, Mail, Plus, Settings, Trash2,
  X, ArrowRightLeft, Loader2, AlertTriangle,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { DataTable, DataTableColumn, DataTableAction } from '@/components/admin/DataTable'
import { apiClient } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'

// Admin Panel Redesign — Organizations section, split out of the old
// monolithic AdminPanel.tsx "organizations" tab. Ports every working feature
// (create org, view details, invite member, promote/demote/remove member)
// and fixes three gaps called out this session:
//   1. Organization name/description were never editable from this dialog.
//   2. apiClient.transferOwnership() existed but was never wired to any UI.
//   3. There was no way to delete an organization at all.
// Also adds the pending-invitations list/cancel UI that the old panel never
// rendered even though apiClient.getOrganizationInvitations/cancelInvitation
// already existed.

interface OrgSummary {
  id: string
  name: string
  description?: string | null
  admin_id?: string
  admin_email?: string
  member_count?: number
  subscription_tier?: string
  request_limit?: number
  requests_used?: number
  created_at?: string
}

interface OrgMember {
  id: string
  email: string
  first_name?: string
  last_name?: string
  role: string
  is_active?: boolean
}

interface OrgInvitation {
  id: string
  email: string
  role: string
  expires_at?: string
}

const TIER_OPTIONS = [
  { value: 'starter', label: 'Starter (200 requests)' },
  { value: 'professional', label: 'Professional (500 requests)' },
  { value: 'enterprise', label: 'Enterprise (1000 requests)' },
]

const TIER_DEFAULT_LIMIT: Record<string, number> = {
  starter: 200,
  professional: 500,
  enterprise: 1000,
}

export default function OrganizationsSection() {
  const { toast } = useToast()

  const [organizations, setOrganizations] = useState<OrgSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Create org dialog
  const [showCreateOrgDialog, setShowCreateOrgDialog] = useState(false)
  const [newOrg, setNewOrg] = useState({ name: '', description: '', admin_email: '', subscription_tier: 'enterprise' })
  const [creatingOrg, setCreatingOrg] = useState(false)

  // Org details dialog
  const [showOrgDetailsDialog, setShowOrgDetailsDialog] = useState(false)
  const [orgDetailsLoading, setOrgDetailsLoading] = useState(false)
  const [selectedOrg, setSelectedOrg] = useState<OrgSummary | null>(null)
  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([])
  const [orgInvitations, setOrgInvitations] = useState<OrgInvitation[]>([])
  const [savingOrg, setSavingOrg] = useState(false)

  // Invite member
  const [orgInvite, setOrgInvite] = useState({ email: '', role: 'org_member' })
  const [invitingMember, setInvitingMember] = useState(false)
  const [cancelingInviteId, setCancelingInviteId] = useState<string | null>(null)

  // Member role change
  const [updatingMemberId, setUpdatingMemberId] = useState<string | null>(null)

  // Remove member confirm
  const [memberPendingRemoval, setMemberPendingRemoval] = useState<OrgMember | null>(null)
  const [removingMember, setRemovingMember] = useState(false)

  // Transfer ownership confirm
  const [memberPendingTransfer, setMemberPendingTransfer] = useState<OrgMember | null>(null)
  const [transferringOwnership, setTransferringOwnership] = useState(false)

  // Delete organization confirm
  const [orgPendingDelete, setOrgPendingDelete] = useState<OrgSummary | null>(null)
  const [deletingOrg, setDeletingOrg] = useState(false)

  useEffect(() => {
    loadOrganizations()
  }, [])

  const loadOrganizations = async () => {
    try {
      setLoading(true)
      setError(null)
      const orgs = (await apiClient.getAllOrganizations(0, 100)) as OrgSummary[]
      setOrganizations(Array.isArray(orgs) ? orgs : [])
    } catch (e: any) {
      console.error('Failed to load organizations', e)
      setError(e?.message || 'Failed to load organizations')
    } finally {
      setLoading(false)
    }
  }

  const openOrgDetails = async (orgId: string) => {
    setShowOrgDetailsDialog(true)
    setOrgDetailsLoading(true)
    try {
      const [details, invitesRes] = await Promise.all([
        apiClient.getOrganizationDetails(orgId) as Promise<OrgSummary & { members?: OrgMember[] }>,
        apiClient.getOrganizationInvitations(orgId).catch(() => ({ invitations: [] })) as Promise<{ invitations?: OrgInvitation[] }>,
      ])
      setSelectedOrg(details)
      setOrgMembers(details?.members || [])
      setOrgInvitations(invitesRes?.invitations || [])
    } catch (e: any) {
      console.error('Failed to load organization details', e)
      toast({ title: 'Failed to load organization details', description: e?.message, variant: 'destructive' })
      setShowOrgDetailsDialog(false)
    } finally {
      setOrgDetailsLoading(false)
    }
  }

  const closeOrgDetails = (open: boolean) => {
    setShowOrgDetailsDialog(open)
    if (!open) {
      setSelectedOrg(null)
      setOrgMembers([])
      setOrgInvitations([])
      setOrgInvite({ email: '', role: 'org_member' })
    }
  }

  const handleCreateOrg = async () => {
    if (!newOrg.name.trim()) {
      toast({ title: 'Organization name is required', variant: 'destructive' })
      return
    }
    if (!newOrg.admin_email.trim()) {
      toast({ title: 'Admin email is required', description: "Pick the existing user who should own this organization", variant: 'destructive' })
      return
    }
    try {
      setCreatingOrg(true)
      await apiClient.createOrganization(newOrg.name, newOrg.description, newOrg.admin_email, newOrg.subscription_tier)
      setShowCreateOrgDialog(false)
      setNewOrg({ name: '', description: '', admin_email: '', subscription_tier: 'enterprise' })
      await loadOrganizations()
      toast({ title: 'Organization created' })
    } catch (e: any) {
      toast({ title: 'Failed to create organization', description: e?.message, variant: 'destructive' })
    } finally {
      setCreatingOrg(false)
    }
  }

  const handleSaveOrgChanges = async () => {
    if (!selectedOrg) return
    if (!selectedOrg.name?.trim()) {
      toast({ title: 'Organization name cannot be empty', variant: 'destructive' })
      return
    }
    try {
      setSavingOrg(true)
      await apiClient.updateOrganization(selectedOrg.id, {
        name: selectedOrg.name,
        description: selectedOrg.description ?? '',
        subscription_tier: selectedOrg.subscription_tier,
        request_limit: selectedOrg.request_limit,
      })
      toast({ title: 'Organization updated' })
      await loadOrganizations()
      closeOrgDetails(false)
    } catch (e: any) {
      toast({ title: 'Failed to update organization', description: e?.message, variant: 'destructive' })
    } finally {
      setSavingOrg(false)
    }
  }

  const handleOrgInvite = async () => {
    if (!selectedOrg || !orgInvite.email.trim()) return
    try {
      setInvitingMember(true)
      await apiClient.inviteTeamMember(selectedOrg.id, orgInvite.email, orgInvite.role)
      setOrgInvite({ email: '', role: 'org_member' })
      toast({ title: 'Invitation sent' })
      const invitesRes = (await apiClient.getOrganizationInvitations(selectedOrg.id).catch(() => ({ invitations: [] }))) as { invitations?: OrgInvitation[] }
      setOrgInvitations(invitesRes?.invitations || [])
    } catch (e: any) {
      toast({ title: 'Failed to send invitation', description: e?.message, variant: 'destructive' })
    } finally {
      setInvitingMember(false)
    }
  }

  const handleCancelInvitation = async (inviteId: string) => {
    if (!selectedOrg) return
    try {
      setCancelingInviteId(inviteId)
      await apiClient.cancelInvitation(selectedOrg.id, inviteId)
      setOrgInvitations((prev) => prev.filter((i) => i.id !== inviteId))
      toast({ title: 'Invitation cancelled' })
    } catch (e: any) {
      toast({ title: 'Failed to cancel invitation', description: e?.message, variant: 'destructive' })
    } finally {
      setCancelingInviteId(null)
    }
  }

  const handleOrgMemberRoleChange = async (userId: string, role: string) => {
    if (!selectedOrg) return
    try {
      setUpdatingMemberId(userId)
      await apiClient.updateTeamMemberRole(selectedOrg.id, userId, role)
      await openOrgDetails(selectedOrg.id)
      toast({ title: 'Member role updated' })
    } catch (e: any) {
      toast({ title: 'Failed to update role', description: e?.message, variant: 'destructive' })
    } finally {
      setUpdatingMemberId(null)
    }
  }

  const confirmRemoveMember = async () => {
    if (!selectedOrg || !memberPendingRemoval) return
    try {
      setRemovingMember(true)
      await apiClient.removeTeamMember(selectedOrg.id, memberPendingRemoval.id)
      toast({ title: 'Member removed' })
      setMemberPendingRemoval(null)
      await openOrgDetails(selectedOrg.id)
      await loadOrganizations()
    } catch (e: any) {
      toast({ title: 'Failed to remove member', description: e?.message, variant: 'destructive' })
    } finally {
      setRemovingMember(false)
    }
  }

  const confirmTransferOwnership = async () => {
    if (!selectedOrg || !memberPendingTransfer) return
    try {
      setTransferringOwnership(true)
      await apiClient.transferOwnership(selectedOrg.id, memberPendingTransfer.id)
      toast({ title: 'Ownership transferred', description: `${memberPendingTransfer.email} is now the organization admin` })
      setMemberPendingTransfer(null)
      await openOrgDetails(selectedOrg.id)
      await loadOrganizations()
    } catch (e: any) {
      toast({ title: 'Failed to transfer ownership', description: e?.message, variant: 'destructive' })
    } finally {
      setTransferringOwnership(false)
    }
  }

  const confirmDeleteOrg = async () => {
    if (!orgPendingDelete) return
    try {
      setDeletingOrg(true)
      await apiClient.adminDeleteOrganization(orgPendingDelete.id)
      toast({ title: 'Organization deleted', description: 'Members downgraded to freemium and unassigned from the org' })
      setOrgPendingDelete(null)
      if (selectedOrg?.id === orgPendingDelete.id) {
        closeOrgDetails(false)
      }
      await loadOrganizations()
    } catch (e: any) {
      toast({ title: 'Failed to delete organization', description: e?.message, variant: 'destructive' })
    } finally {
      setDeletingOrg(false)
    }
  }

  const columns: DataTableColumn<OrgSummary>[] = [
    {
      key: 'name',
      header: 'Organization',
      render: (org) => (
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Building2 className="w-4 h-4 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="font-medium truncate">{org.name}</p>
            <p className="text-xs text-muted-foreground truncate">{org.admin_email || 'No admin'}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'members',
      header: 'Members',
      render: (org) => <span>{org.member_count ?? 0}</span>,
    },
    {
      key: 'tier',
      header: 'Tier',
      render: (org) => <Badge variant="secondary" className="capitalize">{org.subscription_tier || 'enterprise'}</Badge>,
    },
    {
      key: 'usage',
      header: 'Usage',
      render: (org) => (
        <span className="text-sm text-muted-foreground">
          {org.requests_used ?? 0} / {org.request_limit === -1 ? '∞' : org.request_limit ?? '—'}
        </span>
      ),
    },
  ]

  const rowActions: DataTableAction<OrgSummary>[] = [
    {
      label: 'Manage',
      icon: <Settings className="w-4 h-4 mr-2" />,
      onClick: (org) => openOrgDetails(org.id),
    },
    {
      label: 'Delete Organization',
      icon: <Trash2 className="w-4 h-4 mr-2" />,
      destructive: true,
      onClick: (org) => setOrgPendingDelete(org),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Organization Management</h2>
          <p className="text-muted-foreground mt-1">Manage organization access rights, plans, and settings</p>
        </div>
        <Button onClick={() => setShowCreateOrgDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Organization
        </Button>
      </div>

      {error && (
        <Card className="border-destructive/50">
          <CardContent className="flex items-center gap-2 py-4 text-sm text-destructive">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {error}
          </CardContent>
        </Card>
      )}

      <DataTable
        columns={columns}
        rows={organizations}
        getRowId={(org) => org.id}
        rowActions={rowActions}
        isLoading={loading}
        emptyMessage="No organizations found"
      />

      {/* Create Organization Dialog */}
      <Dialog open={showCreateOrgDialog} onOpenChange={setShowCreateOrgDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Organization</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Organization Name *</Label>
              <Input value={newOrg.name} onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })} placeholder="Acme Corp" />
            </div>
            <div>
              <Label>Description</Label>
              <Input value={newOrg.description} onChange={(e) => setNewOrg({ ...newOrg, description: e.target.value })} placeholder="Optional description" />
            </div>
            <div>
              <Label>Admin Email *</Label>
              <Input value={newOrg.admin_email} onChange={(e) => setNewOrg({ ...newOrg, admin_email: e.target.value })} placeholder="Existing user who should own this org" />
              <p className="text-xs text-muted-foreground mt-1">Must be an existing user's email — they become this org's admin</p>
            </div>
            <div>
              <Label>Subscription Tier</Label>
              <Select value={newOrg.subscription_tier} onValueChange={(v) => setNewOrg({ ...newOrg, subscription_tier: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIER_OPTIONS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Every member of this org inherits this tier</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCreateOrg} className="flex-1" disabled={creatingOrg || !newOrg.name.trim() || !newOrg.admin_email.trim()}>
                {creatingOrg ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Create
              </Button>
              <Button variant="outline" onClick={() => setShowCreateOrgDialog(false)} disabled={creatingOrg}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Organization Details Dialog */}
      <Dialog open={showOrgDetailsDialog} onOpenChange={closeOrgDetails}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {selectedOrg?.name || 'Organization Details'}
            </DialogTitle>
          </DialogHeader>

          {orgDetailsLoading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Loading organization details...
            </div>
          ) : selectedOrg && (
            <div className="space-y-6 mt-4">
              {/* Organization Info */}
              <Card>
                <CardHeader>
                  <CardTitle>Organization Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-muted-foreground">Request Limit</Label>
                      <p className="font-semibold">{selectedOrg.request_limit === -1 ? 'Unlimited' : selectedOrg.request_limit}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Usage</Label>
                      <p className="font-semibold">{selectedOrg.requests_used || 0}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Members</Label>
                      <p className="font-semibold">{orgMembers.length}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Admin</Label>
                      <p className="font-semibold">{selectedOrg.admin_email || 'N/A'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Organization Settings - editable */}
              <Card>
                <CardHeader>
                  <CardTitle>Organization Settings</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">Update organization name, description, access rights, and limits</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Organization Name</Label>
                    <Input
                      className="mt-1"
                      value={selectedOrg.name}
                      onChange={(e) => setSelectedOrg({ ...selectedOrg, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      className="mt-1"
                      value={selectedOrg.description || ''}
                      onChange={(e) => setSelectedOrg({ ...selectedOrg, description: e.target.value })}
                      placeholder="Optional description"
                    />
                  </div>
                  <div>
                    <Label>Subscription Tier</Label>
                    <Select
                      value={selectedOrg.subscription_tier || 'enterprise'}
                      onValueChange={(v) => setSelectedOrg({
                        ...selectedOrg,
                        subscription_tier: v,
                        request_limit: TIER_DEFAULT_LIMIT[v] ?? selectedOrg.request_limit,
                      })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIER_OPTIONS.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">Changing this updates every current member's tier, modules, and request limit too</p>
                  </div>
                  <div>
                    <Label>Request Limit</Label>
                    <Input
                      type="number"
                      value={selectedOrg.request_limit === -1 ? '' : selectedOrg.request_limit ?? ''}
                      placeholder="Unlimited (-1)"
                      onChange={(e) => {
                        const value = e.target.value === '' || e.target.value === '-1' ? -1 : parseInt(e.target.value, 10)
                        setSelectedOrg({ ...selectedOrg, request_limit: value })
                      }}
                      className="mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Set to -1 for unlimited requests (overridden by tier change above)</p>
                  </div>
                  <Button onClick={handleSaveOrgChanges} className="w-full" disabled={savingOrg}>
                    {savingOrg && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Save Changes
                  </Button>
                </CardContent>
              </Card>

              {/* Team Members */}
              <Card>
                <CardHeader>
                  <CardTitle>Team Members ({orgMembers.length})</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">Manage this organization's members as platform admin</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-2 items-end p-3 border border-border rounded-lg bg-muted/30">
                    <div className="flex-1">
                      <Label className="text-xs">Invite by email</Label>
                      <Input
                        value={orgInvite.email}
                        onChange={(e) => setOrgInvite({ ...orgInvite, email: e.target.value })}
                        placeholder="teammate@company.com"
                        className="mt-1"
                      />
                    </div>
                    <div className="w-40">
                      <Label className="text-xs">Role</Label>
                      <Select value={orgInvite.role} onValueChange={(v) => setOrgInvite({ ...orgInvite, role: v })}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="org_member">Member</SelectItem>
                          <SelectItem value="org_admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={handleOrgInvite} disabled={invitingMember || !orgInvite.email.trim()}>
                      {invitingMember ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                      Invite
                    </Button>
                  </div>

                  <div className="space-y-2">
                    {orgMembers.length === 0 ? (
                      <p className="text-center text-muted-foreground py-8">No members yet</p>
                    ) : (
                      orgMembers.map((member) => {
                        const isOwner = member.id === selectedOrg.admin_id
                        return (
                          <div key={member.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-full ${member.role === 'org_admin' ? 'bg-yellow-100 dark:bg-yellow-900' : 'bg-blue-100 dark:bg-blue-900'}`}>
                                {member.role === 'org_admin' ? (
                                  <Crown className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                                ) : (
                                  <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                )}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">{member.first_name} {member.last_name}</p>
                                  {isOwner && <Badge variant="default" className="gap-1"><Crown className="w-3 h-3" />Owner</Badge>}
                                </div>
                                <p className="text-sm text-muted-foreground">{member.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Select
                                value={member.role === 'org_admin' ? 'org_admin' : 'org_member'}
                                onValueChange={(v) => handleOrgMemberRoleChange(member.id, v)}
                                disabled={updatingMemberId === member.id}
                              >
                                <SelectTrigger className="w-32 h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="org_member">Member</SelectItem>
                                  <SelectItem value="org_admin">Admin</SelectItem>
                                </SelectContent>
                              </Select>
                              {!isOwner && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setMemberPendingTransfer(member)}
                                  title="Transfer ownership to this member"
                                >
                                  <ArrowRightLeft className="w-4 h-4" />
                                </Button>
                              )}
                              {!isOwner && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setMemberPendingRemoval(member)}
                                >
                                  Remove
                                </Button>
                              )}
                            </div>
                          </div>
                        )
                      })
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Pending Invitations */}
              {orgInvitations.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Pending Invitations ({orgInvitations.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {orgInvitations.map((invite) => (
                        <div key={invite.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                          <div className="flex items-center gap-3">
                            <Mail className="w-4 h-4 text-muted-foreground" />
                            <div>
                              <p className="font-medium">{invite.email}</p>
                              <p className="text-xs text-muted-foreground">
                                Invited as {invite.role === 'org_admin' ? 'Admin' : 'Member'}
                              </p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleCancelInvitation(invite.id)}
                            disabled={cancelingInviteId === invite.id}
                          >
                            {cancelingInviteId === invite.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Danger Zone */}
              <Card className="border-destructive/50">
                <CardHeader>
                  <CardTitle className="text-destructive">Danger Zone</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">Irreversible actions for this organization</p>
                </CardHeader>
                <CardContent>
                  <Button variant="destructive" onClick={() => setOrgPendingDelete(selectedOrg)}>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Organization
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Remove Member Confirm */}
      <AlertDialog open={!!memberPendingRemoval} onOpenChange={(open) => !open && setMemberPendingRemoval(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove team member?</AlertDialogTitle>
            <AlertDialogDescription>
              Remove {memberPendingRemoval?.email} from {selectedOrg?.name}? They will lose access to this organization's resources and settings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removingMember}>Cancel</AlertDialogCancel>
            <AlertDialogAction destructive disabled={removingMember} onClick={confirmRemoveMember}>
              {removingMember && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Remove Member
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transfer Ownership Confirm */}
      <AlertDialog open={!!memberPendingTransfer} onOpenChange={(open) => !open && setMemberPendingTransfer(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Transfer ownership?</AlertDialogTitle>
            <AlertDialogDescription>
              Transfer ownership of {selectedOrg?.name} to {memberPendingTransfer?.email}? You will no longer be the org admin.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={transferringOwnership}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={transferringOwnership} onClick={confirmTransferOwnership}>
              {transferringOwnership && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Transfer Ownership
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Organization Confirm */}
      <AlertDialog open={!!orgPendingDelete} onOpenChange={(open) => !open && setOrgPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete organization?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes {orgPendingDelete?.name}. Its {orgPendingDelete?.member_count ?? 0} members will be downgraded to
              the freemium tier and unassigned from the org — their accounts are NOT deleted. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingOrg}>Cancel</AlertDialogCancel>
            <AlertDialogAction destructive disabled={deletingOrg} onClick={confirmDeleteOrg}>
              {deletingOrg && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete Organization
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
